import React, { useState, useMemo, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FiShoppingCart,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiUser,
  FiMapPin,
  FiFileText,
  FiCreditCard,
  FiCheck,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
} from "react-icons/fi";

import { useSelector, useDispatch } from "react-redux";
import {
  selectCart,
  removeFromCart,
  updateQuantity,
  clearCart,
} from "../../../store/slices/cart/slice";
import {
  selectOrders,
  clearCreateSuccess,
  clearOrdersError,
} from "../../../store/slices/orders/slice";
import { createOrder } from "../../../store/slices/orders/asyncThunks";

import MainContainer from "../../../components/utils/MainContainer";
import Particles from "../../../components/utils/Particles";
import {
  CartItem,
  CartLayout,
  CartSection,
  CheckoutForm,
  CheckoutSection,
  Container,
  CurrentPrice,
  EmptyCart,
  ErrorMessage,
  FormGrid,
  FormGroup,
  FormInput,
  FormLabel,
  FormSection,
  FormSectionTitle,
  FormTextarea,
  ItemBand,
  ItemImage,
  ItemInfo,
  ItemName,
  ItemPrice,
  ItemSize,
  OrderSummary,
  PageBtn,
  Pagination,
  QuantityBtn,
  QuantityControl,
  QuantityInput,
  RemoveBtn,
  Row,
  SectionTitle,
  SubmitButton,
  SummaryRow,
  TotalPrice,
} from "./CartPage.styles";
import { formatPrice } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";

export default function CartPage() {
  const dispatch = useDispatch();
  const cart = useSelector(selectCart);
  const orders = useSelector(selectOrders);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zipCode: "",
    details: "",
  });
  const [errors, setErrors] = useState({});

  // Load saved form data from localStorage on component mount
  useEffect(() => {
    const savedFormData = localStorage.getItem("cartFormData");
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        setFormData(parsedData);
      } catch (error) {
        console.error("Error loading saved form data:", error);
      }
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (
      formData.firstName ||
      formData.lastName ||
      formData.email ||
      formData.phone ||
      formData.address ||
      formData.city ||
      formData.zipCode
    ) {
      localStorage.setItem("cartFormData", JSON.stringify(formData));
    }
  }, [formData]);

  const itemsPerPage = 4;
  const totalPages = Math.ceil(cart.items?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = cart.items?.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Calculate totals
  const totals = useMemo(() => {
    const subtotal = cart.totalPrice;
    const shipping = subtotal >= 6000 ? 0 : 500; // Besplatna dostava preko 6000 RSD
    const total = subtotal + shipping;

    return { subtotal, shipping, total };
  }, [cart.totalPrice]);

  const handleUpdateQuantity = (cartItemId, newQuantity) => {
    if (newQuantity < 1) {
      dispatch(removeFromCart(cartItemId));
    } else {
      dispatch(updateQuantity({ cartItemId, quantity: newQuantity }));
    }
  };

  const handleRemoveItem = (cartItemId) => {
    dispatch(removeFromCart(cartItemId));
    // Adjust page if necessary
    if (paginatedItems.length === 1 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Ime je obavezno";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Prezime je obavezno";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email je obavezan";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email adresa nije validna";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefon je obavezan";
    }
    if (!formData.address.trim()) {
      newErrors.address = "Adresa je obavezna";
    }
    if (!formData.city.trim()) {
      newErrors.city = "Grad je obavezan";
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = "Poštanski broj je obavezan";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Clear any previous errors
    dispatch(clearOrdersError());

    // Prepare order data
    const orderData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      zipCode: formData.zipCode,
      details: formData.details,
      products: cart.items?.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        selectedSize: item.selectedSize,
      })),
    };

    try {
      const result = await dispatch(createOrder(orderData)).unwrap();

      navigate("/porudzbina-uspesna", {
        state: {
          orderData: {
            orderNumber: result.data.orderNumber,
            total: result.data.totalPrice,
            items: cart.totalItems,
            customerInfo: formData,
          },
        },
      });

      setTimeout(() => {
        window.scrollTo(0, 0);
        dispatch(clearCart());
        dispatch(clearCreateSuccess());
        // Don't clear form data after successful order - keep it for future orders
      }, 300);
    } catch (error) {
      console.error("Order creation failed:", error.message);
    }
  };

  return (
    <MainContainer>
      <Particles />
      <Container>
        {cart.items?.length === 0 ? (
          <EmptyCart>
            <FiShoppingCart
              size={80}
              style={{ marginBottom: "1rem", color: "#666" }}
            />
            <h3>Korpa je prazna</h3>
            <p>Dodajte proizvode u korpu da biste nastavili sa poručivanjem.</p>
            <button
              onClick={() => navigate("/prodavnica")}
              style={{
                background: "#ff6b35",
                color: "#fff",
                border: "none",
                padding: "1rem 2rem",
                borderRadius: "10px",
                cursor: "pointer",
                marginTop: "1rem",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              Nastavi kupovinu
            </button>
          </EmptyCart>
        ) : (
          <CartLayout>
            <div>
              <CartSection>
                <SectionTitle>
                  <FiShoppingCart />
                  Proizvodi ({cart.totalItems})
                </SectionTitle>

                <AnimatePresence>
                  {paginatedItems?.map((item) => (
                    <CartItem
                      key={item.cartItemId}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ItemImage>
                        <img
                          src={item.mainImage}
                          alt={item.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                          onError={(e) => {
                            // Fallback to initials if image fails
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML =
                              item.band?.name?.substring(0, 3) || "IMG";
                          }}
                        />
                      </ItemImage>
                      <Row>
                        <ItemInfo>
                          <ItemBand>{item.band?.name}</ItemBand>
                          <ItemName>{item.name}</ItemName>
                          {item.selectedSize && (
                            <ItemSize>Veličina: {item.selectedSize}</ItemSize>
                          )}
                        </ItemInfo>
                        <QuantityControl>
                          <QuantityBtn
                            onClick={() =>
                              handleUpdateQuantity(
                                item.cartItemId,
                                item.quantity - 1
                              )
                            }
                            disabled={item.quantity <= 1}
                          >
                            <FiMinus />
                          </QuantityBtn>
                          <QuantityInput
                            value={item.quantity}
                            onChange={(e) =>
                              handleUpdateQuantity(
                                item.cartItemId,
                                parseInt(e.target.value) || 1
                              )
                            }
                            min="1"
                            max={item.stockQuantity}
                          />
                          <QuantityBtn
                            onClick={() =>
                              handleUpdateQuantity(
                                item.cartItemId,
                                item.quantity + 1
                              )
                            }
                            disabled={item.quantity >= item.stockQuantity}
                          >
                            <FiPlus />
                          </QuantityBtn>
                        </QuantityControl>
                      </Row>

                      <ItemPrice>
                        <CurrentPrice>
                          {formatPrice(item.salePrice || item.price)}
                        </CurrentPrice>
                        <TotalPrice>
                          Ukupno:{" "}
                          {formatPrice(
                            (item.salePrice || item.price) * item.quantity
                          )}
                        </TotalPrice>
                      </ItemPrice>

                      <RemoveBtn
                        onClick={() => handleRemoveItem(item.cartItemId)}
                      >
                        <FiTrash2 />
                      </RemoveBtn>
                    </CartItem>
                  ))}
                </AnimatePresence>

                {totalPages > 1 && (
                  <Pagination>
                    <PageBtn
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      <FiChevronLeft />
                    </PageBtn>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <PageBtn
                          key={page}
                          $active={currentPage === page}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </PageBtn>
                      )
                    )}

                    <PageBtn
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      <FiChevronRight />
                    </PageBtn>
                  </Pagination>
                )}
              </CartSection>
            </div>

            <div>
              <CheckoutSection>
                <SectionTitle>
                  <FiCreditCard />
                  Poručivanje
                </SectionTitle>

                <OrderSummary>
                  <SummaryRow>
                    <span className="label">Proizvodi:</span>
                    <span className="value">
                      {formatPrice(totals.subtotal)}
                    </span>
                  </SummaryRow>
                  <SummaryRow>
                    <span className="label">Dostava:</span>
                    <span className="value">
                      {totals.shipping === 0
                        ? "BESPLATNO"
                        : formatPrice(totals.shipping)}
                    </span>
                  </SummaryRow>
                  {totals.shipping > 0 && totals.subtotal < 6000 && (
                    <SummaryRow
                      style={{
                        fontSize: "0.8rem",
                        color: "#ff6b35",
                        fontStyle: "italic",
                      }}
                    >
                      <span colSpan={2}>
                        Dodajte još {formatPrice(6000 - totals.subtotal)} za
                        besplatnu dostavu
                      </span>
                    </SummaryRow>
                  )}
                  <SummaryRow className="total">
                    <span className="label">UKUPNO:</span>
                    <span className="value">{formatPrice(totals.total)}</span>
                  </SummaryRow>
                </OrderSummary>

                {orders.error && (
                  <ErrorMessage>
                    <FiAlertCircle size={16} />
                    {orders.error}
                  </ErrorMessage>
                )}

                <CheckoutForm onSubmit={handleSubmit}>
                  <FormSection>
                    <FormSectionTitle>
                      <FiUser />
                      Lični podaci
                    </FormSectionTitle>
                    <FormGrid>
                      <FormGroup>
                        <FormLabel htmlFor="firstName">Ime</FormLabel>
                        <FormInput
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Vaše ime"
                          className={errors.firstName ? "error" : ""}
                          required
                        />
                        {errors.firstName && (
                          <ErrorMessage>
                            <FiAlertCircle size={12} />
                            {errors.firstName}
                          </ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <FormLabel htmlFor="lastName">Prezime</FormLabel>
                        <FormInput
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Vaše prezime"
                          className={errors.lastName ? "error" : ""}
                          required
                        />
                        {errors.lastName && (
                          <ErrorMessage>
                            <FiAlertCircle size={12} />
                            {errors.lastName}
                          </ErrorMessage>
                        )}
                      </FormGroup>
                    </FormGrid>

                    <FormGrid>
                      <FormGroup>
                        <FormLabel htmlFor="email">Email</FormLabel>
                        <FormInput
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="vas@email.rs"
                          className={errors.email ? "error" : ""}
                          required
                        />
                        {errors.email && (
                          <ErrorMessage>
                            <FiAlertCircle size={12} />
                            {errors.email}
                          </ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <FormLabel htmlFor="phone">Telefon</FormLabel>
                        <FormInput
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="06x xxx xxxx"
                          className={errors.phone ? "error" : ""}
                          required
                        />
                        {errors.phone && (
                          <ErrorMessage>
                            <FiAlertCircle size={12} />
                            {errors.phone}
                          </ErrorMessage>
                        )}
                      </FormGroup>
                    </FormGrid>
                  </FormSection>

                  <FormSection>
                    <FormSectionTitle>
                      <FiMapPin />
                      Adresa dostave
                    </FormSectionTitle>

                    <FormGroup>
                      <FormLabel htmlFor="address">Adresa</FormLabel>
                      <FormInput
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Ulica i broj"
                        className={errors.address ? "error" : ""}
                        required
                      />
                      {errors.address && (
                        <ErrorMessage>
                          <FiAlertCircle size={12} />
                          {errors.address}
                        </ErrorMessage>
                      )}
                    </FormGroup>

                    <FormGrid>
                      <FormGroup>
                        <FormLabel htmlFor="city">Grad</FormLabel>
                        <FormInput
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Grad"
                          className={errors.city ? "error" : ""}
                          required
                        />
                        {errors.city && (
                          <ErrorMessage>
                            <FiAlertCircle size={12} />
                            {errors.city}
                          </ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <FormLabel htmlFor="zipCode">Poštanski broj</FormLabel>
                        <FormInput
                          id="zipCode"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          placeholder="34000"
                          className={errors.zipCode ? "error" : ""}
                          required
                        />
                        {errors.zipCode && (
                          <ErrorMessage>
                            <FiAlertCircle size={12} />
                            {errors.zipCode}
                          </ErrorMessage>
                        )}
                      </FormGroup>
                    </FormGrid>
                  </FormSection>

                  <FormSection>
                    <FormSectionTitle>
                      <FiFileText />
                      Napomena
                    </FormSectionTitle>

                    <FormGroup>
                      <FormTextarea
                        id="details"
                        name="details"
                        value={formData.details}
                        onChange={handleInputChange}
                        placeholder="Dodatne napomene za porudžbinu (opciono)"
                      />
                    </FormGroup>
                  </FormSection>

                  <SubmitButton
                    type="submit"
                    className={`
                      ${orders.createLoading ? "loading" : ""}
                      ${orders.createSuccess ? "success" : ""}
                    `}
                    disabled={orders.createLoading}
                  >
                    {orders.createLoading ? (
                      <>
                        <FiLoader />
                        Obrađujem...
                      </>
                    ) : orders.createSuccess ? (
                      <>
                        <FiCheck />
                        Porudžbina je poslana!
                      </>
                    ) : (
                      <>
                        <FiCheck />
                        Potvrdi porudžbinu
                      </>
                    )}
                  </SubmitButton>
                </CheckoutForm>
              </CheckoutSection>
            </div>
          </CartLayout>
        )}
      </Container>
    </MainContainer>
  );
}
